<template>
  <div class="project-detail" v-if="project">
    <div class="header">
      <h1>{{ project.title }}</h1>
      <p class="period">{{ project.period }}</p>
    </div>

    <div class="section" v-if="project.description">
      <h3>Description</h3>
      <p>{{ project.description }}</p>
    </div>

    <div class="section" v-if="project.features && project.features.length">
      <h3>Features</h3>
      <ul>
        <li v-for="(feature, index) in project.features" 
            :key="index">{{ feature }}</li>
      </ul>
    </div>

    <div class="section" v-if="project.technologies && project.technologies.length">
      <h3>Technologies</h3>
      <div class="technologies">
        <span v-for="tech in project.technologies" 
              :key="tech" 
              class="tech-tag">
          {{ tech }}
        </span>
      </div>
    </div>

    <div class="section" v-if="project.outcomes && project.outcomes.length">
      <h3>Outcomes</h3>
      <ul>
        <li v-for="(outcome, index) in project.outcomes" 
            :key="index">{{ outcome }}</li>
      </ul>
    </div>

    <!-- Link Preview Section -->
    <div class="section" v-if="projectLinks.length">
      <h3>Ссылка{{ projectLinks.length > 1 ? 'и' : '' }}</h3>
      <div class="link-preview" v-for="(link, index) in projectLinks" :key="index">
        <a :href="getFullUrl(link)" target="_blank" class="link-preview-container">
          <div class="link-preview-icon">
            <span v-if="isGithubLink(link)">
              <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
              </svg>
            </span>
            <span v-else-if="isTelegramLink(link)">
              <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21.5 2l-19 10 5.5 2 2 6.5 4-5 6 4L21.5 2z"></path>
                <path d="M10.5 14l1 3.5L13 13l4.5 5.5"></path>
              </svg>
            </span>
            <span v-else-if="isVkLink(link)">
              <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 1a11 11 0 1 0 0 22 11 11 0 1 0 0-22z"></path>
                <path d="M6 12h5l1-3 1 3h5"></path>
              </svg>
            </span>
            <span v-else>
              <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
              </svg>
            </span>
          </div>
          <div class="link-preview-content">
            <div class="link-preview-title">{{ getLinkTitle(link) }}</div>
            <div class="link-preview-url">{{ formatUrl(link) }}</div>
          </div>
          <div class="link-preview-arrow">
            <span class="external-icon">↗</span>
          </div>
        </a>
      </div>
    </div>

    <router-link to="/projects" class="back-link">
      <span class="arrow">←</span> Назад к проектам
    </router-link>
  </div>
  <div v-else class="not-found">
    <h1>Project Not Found</h1>
    <router-link to="/projects" class="back-link">
      <span class="arrow">←</span> Назад к проектам
    </router-link>
  </div>
</template>

<script>
import { projects } from '../stores/projects';

export default {
  name: 'ProjectDetail',
  data() {
    return {
      project: projects.find(project => project.id === this.$route.params.id)
    };
  },
  computed: {
    projectLinks() {
      if (!this.project || !this.project.link) return [];
      
      // Handle both string and array types
      return Array.isArray(this.project.link) 
        ? this.project.link 
        : [this.project.link];
    }
  },
  methods: {
    isGithubLink(link) {
      return link.includes('github.com');
    },
    isVkLink(link) {
      return link.includes('vk.com');
    },
    isTelegramLink(link) {
      return link.includes('t.me') || link.includes('telegram');
    },
    getLinkTitle(link) {
      if (this.isGithubLink(link)) {
        return 'GitHub Repository';
      } else if (this.isVkLink(link)) {
        return 'VK Post';
      } else if (this.isTelegramLink(link)) {
        return 'Telegram Channel';
      } else if (link.includes('web.archive.org')) {
        return 'Agrohub (Web Archive)';
      } else {
        return 'Project Link';
      }
    },
    formatUrl(url) {
      if (!url) return '';
      
      // Remove protocol and trailing slashes
      return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
    },
    getFullUrl(url) {
      // Add protocol if missing
      if (url && !url.startsWith('http')) {
        return 'https://' + url;
      }
      return url;
    }
  }
};
</script>

<style scoped>
.project-detail {
  max-width: 800px;
  margin: 2rem auto;
  padding: 2rem;
}

.header {
  margin-bottom: 2rem;
}

h1 {
  color: #4ec9b0;
  margin: 0;
}

.period {
  color: #6a9955;
  font-size: 1rem;
  margin: 0.5rem 0 0 0;
}

.section {
  margin: 2rem 0;
}

h3 {
  color: #569cd6;
  margin: 0 0 1rem 0;
}

p {
  color: #d4d4d4;
  line-height: 1.6;
}

ul {
  color: #d4d4d4;
  padding-left: 1.5rem;
  line-height: 1.6;
}

li {
  margin-bottom: 0.5rem;
}

.technologies {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.tech-tag {
  background-color: #2d2d2d;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  color: #ce9178;
}

/* Link Preview Styles */
.link-preview {
  margin-bottom: 1rem;
}

.link-preview:last-child {
  margin-bottom: 0;
}

.link-preview-container {
  display: flex;
  background-color: #2d2d2d;
  border: 1px solid #444;
  border-radius: 8px;
  padding: 1rem;
  color: inherit;
  text-decoration: none;
  transition: all 0.3s ease;
  overflow: hidden;
  align-items: center;
}

.link-preview-container:hover {
  background-color: #363636;
  border-color: #555;
  transform: translateY(-2px);
}

.link-preview-icon {
  margin-right: 1rem;
  color: #4ec9b0;
  flex-shrink: 0;
}

.link-preview-content {
  flex: 1;
}

.link-preview-title {
  font-weight: 500;
  color: #4ec9b0;
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
}

.link-preview-url {
  color: #9cdcfe;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  word-break: break-all;
}

.link-preview-arrow {
  margin-left: 1rem;
  color: #4ec9b0;
  font-size: 1.5rem;
  transition: transform 0.2s ease;
}

.link-preview-container:hover .link-preview-arrow {
  transform: translateX(2px) translateY(-2px);
}

.back-link {
  display: inline-flex;
  align-items: center;
  margin-top: 2rem;
  color: #4ec9b0;
  text-decoration: none;
  transition: color 0.3s;
  gap: 0.5rem;
}

.back-link:hover {
  color: #9cdcfe;
}

.arrow {
  font-size: 1.2rem;
}

.not-found {
  text-align: center;
  padding: 4rem 2rem;
  color: #d4d4d4;
}

@media (max-width: 576px) {
  .link-preview-container {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .link-preview-icon {
    margin-bottom: 1rem;
  }
  
  .link-preview-arrow {
    display: none;
  }
}
</style> 